import { jsPDF } from "jspdf";
import QRCode from "qrcode";

export const makeQrDataUrl = (value) =>
  QRCode.toDataURL(value, { margin: 1, width: 240, color: { dark: "#2c221e", light: "#ffffff" } });

export const qrPayload = (booking) => `${window.location.origin}/verify?ref=${booking.booking_id}`;

let emblemCache = null;
const loadEmblem = async () => {
  if (emblemCache !== null) return emblemCache;
  try {
    const blob = await (await fetch("/ttd-emblem.png")).blob();
    emblemCache = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    emblemCache = "";
  }
  return emblemCache;
};

export const getPilgrims = (booking) =>
  booking.pilgrim_details?.length
    ? booking.pilgrim_details
    : [{ name: booking.name, gender: "-", age: "-", id_type: "Aadhaar", id_number: "-" }];

export const fmtTicketDate = (iso) => {
  if (!iso) return "-";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
};

export const to12h = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${suffix}`;
};

export const perTicketAmount = (booking, count) =>
  Math.round(Number(booking.amount) / Math.max(1, count));

export const sevaCode = (service) =>
  service.includes("Special Entry")
    ? "SED"
    : service.split(/\s+/).map((w) => w[0] || "").join("").replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase();

export const reportingFor = (service, slot) => {
  const place = service.includes("Special Entry") ? "ATC Circle" : "Vaikuntham Queue Complex I";
  if (slot) return { time: to12h(slot), place };
  return service.includes("Special Entry")
    ? { time: "6:00 PM", place }
    : { time: "4:00 AM", place };
};

const drawBarcode = (doc, x, y, width, height, value) => {
  const bars = value.split("").flatMap((char, index) => {
    const seed = char.charCodeAt(0) + index * 13;
    return [1 + (seed % 3), 1, 1 + ((seed >> 2) % 2), 1, 2 + ((seed >> 3) % 2)];
  });
  const total = bars.reduce((sum, bar) => sum + bar, 0);
  let cursor = x;
  bars.forEach((bar, index) => {
    const barWidth = (bar / total) * width;
    if (index % 2 === 0) doc.rect(cursor, y, barWidth, height, "F");
    cursor += barWidth;
  });
};

const stubInstructions = [
  "1. At the time of entry, all the pilgrims should carry individual ticket and",
  "   shall produce the same original photo ID used during booking.",
  "2. Pilgrims shall wear traditional dress only. Male: Dhoti/Shirt/Kurtha,",
  "   Pyjama. Female: Saree/Half saree/Chudidar with dupatta.",
  "3. All the pilgrims shall report as per their time slot only and should not",
  "   carry any luggage/electronic gadgets while reporting.",
  "4. Pilgrims having darshan slot will be allowed through Alipiri/Srivari Mettu",
  "   footpaths only after 9 AM on the previous day of darshan.",
  "5. Pilgrims shall collect the Prasadam within 24 Hrs from the reporting time.",
  "6. Cancellation is allowed only through the booking portal before the visit date.",
  "7. TTD reserves the right of cancellation of Darshan under special circumstances.",
];

export const downloadTicketPdf = async (booking) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setProperties({ title: `${booking.booking_id} - Download PDF e-ticket` });
  const pilgrims = getPilgrims(booking);
  const { time: reporting, place: reportAt } = reportingFor(booking.service, booking.slot);
  const qr = await makeQrDataUrl(qrPayload(booking));
  const emblem = await loadEmblem();
  const price = perTicketAmount(booking, pilgrims.length);
  const code = sevaCode(booking.service);

  pilgrims.forEach((pilgrim, index) => {
    const position = index % 3;
    if (index > 0 && position === 0) doc.addPage();
    const top = 5 + position * 96;
    const left = 8;
    const split = 107;
    const width = 194;
    const center = left + (split - left) / 2;
    const ticketId = `${booking.booking_id}${index + 1}`;

    doc.setDrawColor(38, 38, 38);
    doc.setLineWidth(0.35);
    doc.rect(left, top, width, 90);
    doc.line(split, top, split, top + 90);

    doc.setTextColor(139, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.text("Tirumala Tirupati Devasthanams", center, top + 7, { align: "center" });
    doc.setFontSize(booking.service.length > 34 ? 6 : 7.5);
    doc.text(`${booking.service} (Rs.${price})`, center, top + 12.5, { align: "center" });

    if (emblem) {
      doc.addImage(emblem, "PNG", left + 3, top + 3.5, 11.5, 12.7);
    } else {
      doc.setFillColor(48, 151, 105);
      doc.circle(left + 9, top + 10.5, 5, "F");
    }

    doc.setFillColor(44, 34, 30);
    drawBarcode(doc, center - 27.5, top + 16, 55, 10, ticketId);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    doc.setTextColor(44, 34, 30);
    doc.text(ticketId, center, top + 29.5, { align: "center" });

    doc.setDrawColor(90, 90, 90);
    doc.setLineWidth(0.25);
    doc.rect(left + 10, top + 32, 80, 14);
    doc.line(left + 10, top + 39, left + 90, top + 39);
    doc.line(left + 33, top + 32, left + 33, top + 46);
    doc.setFontSize(5.5);
    doc.setTextColor(90, 90, 90);
    doc.text("Reporting Time:", left + 12, top + 37);
    doc.text("Report At:", left + 12, top + 44);
    doc.setTextColor(139, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.text(`${reporting} / ${fmtTicketDate(booking.date)}`, left + 35, top + 37);
    doc.text(reportAt, left + 35, top + 44);

    doc.setTextColor(44, 34, 30);
    doc.setFontSize(7);
    let y = top + 53;
    doc.text("No. of free laddus: 1", left + 10, y);
    doc.text("Amount:", split - 34, y);
    doc.setTextColor(139, 0, 0);
    doc.text(`Rs. ${price}`, split - 6, y, { align: "right" });
    y += 5.5;
    doc.setTextColor(44, 34, 30);
    doc.text("Group Booking Id:", left + 10, y);
    doc.setTextColor(139, 0, 0);
    doc.text(String(booking.booking_id), left + 41, y);
    y += 5.5;
    doc.setTextColor(44, 34, 30);
    doc.text("Booking Date:", left + 10, y);
    doc.setTextColor(139, 0, 0);
    doc.text(fmtTicketDate(booking.date), left + 37, y);
    y += 5.5;
    doc.setTextColor(44, 34, 30);
    doc.text("Pilgrim Name:", left + 10, y);
    doc.text(String(pilgrim.name).toUpperCase(), left + 37, y);
    y += 5.5;
    doc.text("Gender/Age:", left + 10, y);
    doc.text(`${String(pilgrim.gender).toUpperCase()} / ${pilgrim.age}`, left + 37, y);
    y += 5.5;
    doc.text("Photo ID Number:", left + 10, y);
    doc.text(`${String(pilgrim.id_type).toUpperCase()} / ${String(pilgrim.id_number).toUpperCase()}`, left + 41, y);

    doc.addImage(qr, "PNG", split - 30, top + 50, 20, 20);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(4.8);
    doc.setTextColor(110, 110, 110);
    doc.text("NOTE: This is an electronically generated document and does not require any signature.", left + 10, top + 85);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(5.5);
    doc.setTextColor(139, 0, 0);
    doc.text("Executive Officer, Tirumala Tirupati Devasthanams", left + 10, top + 88.5);

    doc.setDrawColor(44, 34, 30);
    doc.setLineWidth(0.6);
    doc.rect(split + 5, top + 7, 32, 12);
    doc.rect(split + 71, top + 7, 15, 12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(139, 0, 0);
    doc.text(reporting, split + 21, top + 15, { align: "center" });
    doc.text(code, split + 78.5, top + 15, { align: "center" });
    doc.setFontSize(6.5);
    doc.setTextColor(44, 34, 30);
    doc.text("Important Information To The Pilgrims", split + 5, top + 26);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(5);
    stubInstructions.forEach((line, i) => doc.text(line, split + 5, top + 31 + i * 4.2));

    if (position < 2 && index < pilgrims.length - 1) {
      doc.setLineDashPattern([1.5, 1.5], 0);
      doc.setDrawColor(120, 120, 120);
      doc.setLineWidth(0.3);
      doc.line(4, top + 93, 206, top + 93);
      doc.setLineDashPattern([], 0);
    }
  });
  doc.save(`${booking.booking_id}-e-ticket.pdf`);
};
